/**
 * Значки кабинета — один набор на весь кабинет.
 *
 * ═══════════════════════════════════════════════════════════════════════
 * ПОЧЕМУ ОНИ НАРИСОВАНЫ, А НЕ НАБРАНЫ ЗНАКАМИ ЮНИКОДА.
 *
 * До переоформления половина значков была буквами: `◷` вместо часов,
 * `⇡` вместо пополнения, `⊘` вместо лимита, `↺` вместо обновления,
 * `■` у завершения, `▸` у разбора. Выглядит это дёшево не «на вкус»,
 * а по устройству: каждый такой знак рисуется ШРИФТОМ, а шрифты
 * рисуют их по-разному — у одного человека кружок часов тонкий,
 * у другого жирный, у третьего вместо знака пустой прямоугольник.
 * Ни толщина штриха, ни размер, ни выравнивание по строке нам
 * при этом не подчиняются.
 *
 * Здесь всё нарисовано в одной сетке 16×16, штрихом 1.6 со скруглёнными
 * концами и в `currentColor` — цвет берут от родителя, поэтому в тёмной
 * теме не правится ничего.
 *
 * ЗНАЧОК НИКОГДА НЕ СТОИТ БЕЗ ПОДПИСИ ИЛИ ПОДСКАЗКИ. Значок — загадка,
 * которую разгадывают один раз; цена неверной разгадки здесь равна цене
 * действия, а действия тут двигают деньги.
 * ═══════════════════════════════════════════════════════════════════════
 */

export interface IconProps {
  /** Размер стороны. По умолчанию 16 — сетка, в которой всё нарисовано. */
  size?: number;
  className?: string;
  style?: React.CSSProperties;
  /**
   * ═══════════════════════════════════════════════════════════════════
   * ТОЛЩИНА ШТРИХА — ПРОСЬБОЙ СНАРУЖИ, А НЕ ЧИСЛОМ ВНУТРИ ФИГУРЫ.
   *
   * 16 точек и 30 — разные рисунки одного значка. Штрих 1.5, верный
   * на шестнадцати, на тридцати даёт жирную лупу, которая спорит
   * с текстом за внимание, — и эталон рисует крупные знаки пустоты
   * штрихом 1.2.
   *
   * До 27.08 это было написано в `ЗнакПустоты` стилем на обёртке
   * (`strokeWidth: 1.2`) и НЕ РАБОТАЛО НИ РАЗУ: `IconSearch` ставила
   * 1.5 на самих фигурах, а объявление на фигуре бьёт наследование
   * от родителя. Знак пустоты полгода выходил жирнее эталонного,
   * и увидеть это можно было только глазом на снимке.
   * ═══════════════════════════════════════════════════════════════════
   */
  штрих?: number;
}

function Svg({
  size = 16,
  className,
  style,
  children,
  filled,
  штрих = 1.6,
}: IconProps & { children: React.ReactNode; filled?: boolean }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill={filled ? 'currentColor' : 'none'}
      stroke={filled ? undefined : 'currentColor'}
      strokeWidth={filled ? undefined : штрих}
      aria-hidden="true"
      className={className}
      style={{ flex: '0 0 auto', ...style }}
    >
      {children}
    </svg>
  );
}

/**
 * ═══════════════════════════════════════════════════════════════════════
 * «НАСТРОИТЬ» — ПОЛЗУНКИ, А НЕ ШЕСТЕРЁНКА. ЗАМЕЧАНИЕ ЗАКАЗЧИКА 28.08.2026.
 *
 * ЗДЕСЬ БЫЛА ШЕСТЕРЁНКА С ВОСЕМЬЮ ЛУЧАМИ. В пятнадцать точек — а именно
 * столько ей отведено в углу плитки — лучи сливаются с кругом, и знак
 * читается как СОЛНЦЕ. Заказчик так его и прочёл.
 *
 * ПОЛЗУНКИ ЧИТАЮТСЯ В ЛЮБОМ РАЗМЕРЕ: две горизонтальные черты
 * с бегунками на них. Смысл тот же — «настройки», — а спутать не с чем:
 * ни одного круглого знака рядом в кабинете нет.
 *
 * Имя `IconGear` оставлено нарочно: оно про НАЗНАЧЕНИЕ, а не про
 * рисунок, и стоит в двух десятках мест. Переименование ради смены
 * восьми лучей на две черты — правка, которую нечем проверить.
 * ═══════════════════════════════════════════════════════════════════════
 */
export function IconGear(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M2.6 5.2h10.8M2.6 10.8h10.8" strokeLinecap="round" strokeWidth="1.5" />
      {/* Бегунки залиты цветом подложки, а не пусты: пустые круги
          на черте читаются как звенья цепи. */}
      <circle cx="6" cy="5.2" r="1.7" fill="var(--bg-surface)" strokeWidth="1.5" />
      <circle cx="10.2" cy="10.8" r="1.7" fill="var(--bg-surface)" strokeWidth="1.5" />
    </Svg>
  );
}

/** Обновить — спросить у Wildberries заново. */
export function IconRefresh(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M13.5 8a5.5 5.5 0 1 1-1.6-3.9" strokeLinecap="round" strokeWidth="1.5" />
      <path d="M13.6 1.9v3.2h-3.2" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" />
    </Svg>
  );
}

/**
 * Запрет — исключить запрос из показов.
 *
 * Кружок с косой чертой, ровно как в эталоне (ответ К-102): круг
 * радиусом 5.6 в сетке 16 и черта из угла в угол. Стоял знак `⊘`
 * буквой — шрифт рисовал его то тонким, то жирным, а рядом с ним
 * в той же строке жила ТЕКСТОВАЯ кнопка «Исключить», делавшая
 * то же самое. Дизайнер убрал удвоение: значок исключает, слово
 * остаётся только у возврата.
 */
export function IconBan({ size = 14, ...p }: IconProps) {
  return (
    <Svg size={size} {...p}>
      <circle cx="8" cy="8" r="5.6" strokeWidth="1.5" />
      <path d="M4 12 12 4" strokeLinecap="round" strokeWidth="1.5" />
    </Svg>
  );
}

/**
 * Стрелка возврата — вернуть запрос в показы.
 *
 * Отдельно от `IconRefresh`: там круг обновления, здесь стрелка,
 * уходящая назад и загибающаяся вниз. Разные действия — разные знаки,
 * иначе «обновить» и «вернуть» читаются одним.
 */
export function IconReturn({ size = 13, ...p }: IconProps) {
  return (
    <Svg size={size} {...p}>
      <path
        d="M6 3.5 2.5 7 6 10.5M2.5 7h7.2a3.3 3.3 0 0 1 0 6.6H6"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.6"
      />
    </Svg>
  );
}

/**
 * Звезда — в избранное: биддер не трогает ставку этого запроса.
 *
 * ЗАЛИТАЯ ВСЕГДА, а разницу «в избранном или нет» показывает ЦВЕТ:
 * `--line-strong` у обычного, цвет марки у отмеченного. Так в эталоне.
 * Контурная звезда рядом с залитой читалась бы как другой значок,
 * а не как то же самое в другом состоянии.
 */
export function IconStar({ size = 14, ...p }: IconProps) {
  return (
    <Svg size={size} filled {...p}>
      <path d="M8 1.8 9.9 5.7l4.3.6-3.1 3 .7 4.3L8 11.6l-3.8 2 .7-4.3-3.1-3 4.3-.6z" />
    </Svg>
  );
}

/** Плюс — положить денег. */
export function IconPlus(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M8 3.2v9.6M3.2 8h9.6" strokeLinecap="round" />
    </Svg>
  );
}

/** Часы — расписание показов. */
export function IconClock(p: IconProps) {
  return (
    <Svg {...p}>
      <circle cx="8" cy="8" r="5.6" strokeWidth="1.5" />
      <path d="M8 4.8V8l2.2 1.4" strokeLinecap="round" strokeWidth="1.5" />
    </Svg>
  );
}

/** Стрелка вверх — автопополнение счёта. */
export function IconTopup(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M8 13V3.6M4.6 7 8 3.4 11.4 7" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" />
    </Svg>
  );
}

/** Перечёркнутый круг — лимит расхода. */
export function IconCap(p: IconProps) {
  return (
    <Svg {...p}>
      <circle cx="8" cy="8" r="5.6" strokeWidth="1.5" />
      <path d="M4 12 12 4" strokeLinecap="round" strokeWidth="1.5" />
    </Svg>
  );
}

/** Молния — правила исключения: сработало и исключило запрос. */
export function IconBolt(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M9 1.6 3.8 9h3.4l-.6 5.4L12.2 7H8.6z" strokeLinejoin="round" strokeWidth="1.5" />
    </Svg>
  );
}

/**
 * Ползунки — «Настроить правило».
 *
 * НЕ ШЕСТЕРЁНКА, И ЭТО РАЗНИЦА ПО СУЩЕСТВУ. Шестерёнкой в кабинете
 * помечена настройка ВОЗМОЖНОСТИ (плитка открывает окно), а здесь
 * правится ПОРОГ одного правила. Один и тот же значок на двух разных
 * действиях приучает не читать подпись.
 *
 * Путь — из эталона 15.09.2026, кнопка `кнопка-настроить-правило`.
 */
export function IconSliders({ size = 14, ...p }: IconProps) {
  return (
    <Svg size={size} {...p}>
      <path d="M2.6 5h10.8M2.6 11h10.8" strokeLinecap="round" strokeWidth="1.5" />
      <circle cx="6" cy="5" r="1.7" strokeWidth="1.5" />
      <circle cx="10.4" cy="11" r="1.7" strokeWidth="1.5" />
    </Svg>
  );
}

/** Квадрат — завершение кампании. Залитый: это не действие, а точка. */
export function IconStop({ size = 9, ...p }: IconProps) {
  return (
    <Svg size={size} filled {...p}>
      <rect x="2" y="2" width="12" height="12" rx="2" />
    </Svg>
  );
}

export function IconPlay({ size = 10, ...p }: IconProps) {
  return (
    <Svg size={size} filled {...p}>
      <path d="M3 2l10 6-10 6z" />
    </Svg>
  );
}

export function IconPause({ size = 10, ...p }: IconProps) {
  return (
    <Svg size={size} filled {...p}>
      <rect x="2.5" y="2" width="4" height="12" rx="1.2" />
      <rect x="9.5" y="2" width="4" height="12" rx="1.2" />
    </Svg>
  );
}

/** Уголок вниз. Повёрнутый — вправо и вверх: одна фигура на все случаи. */
export function IconChevron({ dir = 'down', ...p }: IconProps & { dir?: 'down' | 'up' | 'left' | 'right' }) {
  const turn = { down: 0, up: 180, left: 90, right: -90 }[dir];
  return (
    <Svg {...p} style={{ transform: `rotate(${turn}deg)`, ...p.style }}>
      <path d="m4.6 6.4 3.4 3.4 3.4-3.4" strokeLinecap="round" strokeLinejoin="round" />
    </Svg>
  );
}

export function IconSearch(p: IconProps) {
  return (
    <Svg штрих={1.5} {...p}>
      <circle cx="7" cy="7" r="4.6" />
      <path d="m10.4 10.4 3.1 3.1" strokeLinecap="round" />
    </Svg>
  );
}

export function IconClose(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="m4.5 4.5 7 7M11.5 4.5l-7 7" strokeLinecap="round" />
    </Svg>
  );
}

export function IconCheck({ size = 12, ...p }: IconProps) {
  return (
    <Svg size={size} filled {...p}>
      <path d="M6.5 11.6 3.2 8.3l1.1-1.1 2.2 2.2 5-5 1.1 1.1z" />
    </Svg>
  );
}

/**
 * Незамкнутое кольцо, вращается — «спрашиваем прямо сейчас».
 *
 * КОНТУР И СКОРОСТЬ — ИЗ ЭТАЛОНА, а не «примерно такие»: радиус 5,4,
 * штрих 1,8, разрыв `24 10`, оборот за 900 мс. Кружок, крутящийся
 * с другой скоростью рядом с эталонным, читается как второй кружок.
 */
export function IconSpin({ size = 13, ...p }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      aria-hidden="true"
      style={{ animation: 'rk-spin 900ms linear infinite', ...p.style }}
    >
      <circle cx="8" cy="8" r="5.4" strokeDasharray="24 10" />
    </svg>
  );
}

/** Треугольник с восклицанием — тревога. Залитый: его надо заметить. */
export function IconAlert({ size = 15, ...p }: IconProps) {
  return (
    <Svg size={size} filled {...p}>
      <path d="M8 1.5 15 14H1zM7.2 6h1.6v4H7.2zm0 5h1.6v1.6H7.2z" />
    </Svg>
  );
}

export function IconColumns(p: IconProps) {
  return (
    <Svg {...p}>
      <rect x="2.2" y="2.6" width="11.6" height="10.8" rx="2" strokeWidth="1.5" />
      <path d="M6.3 2.6v10.8M9.9 2.6v10.8" strokeWidth="1.5" />
    </Svg>
  );
}

/**
 * Три столбика — «перебрать ставку и сравнить».
 *
 * ЗАЛИВКОЙ, А НЕ КОНТУРОМ: значок стоит в строке рядом с полем ставки
 * и должен читаться в тринадцать пикселей. Контурная диаграмма на таком
 * размере превращается в серую кляксу.
 */
export function IconBars({ size = 13, ...p }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="currentColor"
      aria-hidden="true"
      {...p}
    >
      <rect x="2" y="9" width="3" height="5" rx="1" />
      <rect x="6.5" y="5" width="3" height="9" rx="1" />
      <rect x="11" y="2" width="3" height="12" rx="1" />
    </svg>
  );
}

export function IconCalendar(p: IconProps) {
  return (
    <Svg {...p}>
      <rect x="2.2" y="3.4" width="11.6" height="10.4" rx="2" strokeWidth="1.5" />
      <path d="M2.2 6.6h11.6M5.4 1.8v2.4M10.6 1.8v2.4" strokeLinecap="round" strokeWidth="1.5" />
    </Svg>
  );
}

export function IconBurger({ size = 18, ...p }: IconProps) {
  return (
    <Svg size={size} {...p}>
      <path d="M2.2 4h11.6M2.2 8h11.6M2.2 12h11.6" strokeLinecap="round" />
    </Svg>
  );
}

export function IconMoon(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M13 9.5A5.5 5.5 0 0 1 6.5 3a5.5 5.5 0 1 0 6.5 6.5z" strokeLinejoin="round" strokeWidth="1.4" />
    </Svg>
  );
}

export function IconSun(p: IconProps) {
  return (
    <Svg {...p}>
      <circle cx="8" cy="8" r="3.1" strokeWidth="1.4" />
      <path
        d="M8 1.4v1.6M8 13v1.6M14.6 8H13M3 8H1.4M12.7 3.3l-1.1 1.1M4.4 11.6l-1.1 1.1M12.7 12.7l-1.1-1.1M4.4 4.4 3.3 3.3"
        strokeLinecap="round"
        strokeWidth="1.4"
      />
    </Svg>
  );
}

/**
 * Знак БИДРЫ.
 *
 * БУКВА РИСУЕТСЯ КОНТУРОМ, А НЕ НАБИРАЕТСЯ ШРИФТОМ. Набранная «Б»
 * стояла в квадрате криво, и настроить это было нечем: строчный блок
 * центрируется по строке, а у строки сверху и снизу место под выносные
 * элементы, которых у «Б» нет. Буква садилась выше середины — заметно
 * ровно на квадрате в двадцать шесть пикселей, то есть там, где знак
 * и живёт. Контур центрируется сам: у него нет ни строки, ни выносных
 * элементов, только то, что видно. Заодно знак перестал зависеть
 * от того, какой шрифт нашёлся у человека в системе.
 *
 * ТОТ ЖЕ КОНТУР ЛЕЖИТ В `public/favicon.svg` — знак вкладки и знак
 * в шапке обязаны совпадать, а две «Б», нарисованные разными способами,
 * однажды разъедутся. Совпадение сторожит `mark.spec.ts`.
 */

/** Контур «Б» — общий для шапки, входа и значка вкладки. */
export const MARK_PATH =
  'M21.4 9.6h-7.6v4.1h3.6c1.8 0 3.2.4 4.2 1.3 1 .9 1.5 2.1 1.5 3.7 0 1.6-.5 2.9-1.5 3.8-1 .9-2.4 1.4-4.2 1.4h-7V6.6h11V9.6Zm-4 10.7c.9 0 1.5-.2 2-.6.4-.4.6-1 .6-1.8 0-.7-.2-1.3-.6-1.7-.5-.4-1.1-.6-2-.6h-3.6v4.7h3.6Z';

/**
 * Как контур ставится в квадрат 32×32.
 *
 * ЧИСЛА ПОСЧИТАНЫ, А НЕ ПОДОБРАНЫ НА ГЛАЗ: своя рамка у буквы —
 * от (10,4; 6,6) до (23,1; 23,9), то есть 12,7 × 17,3. При множителе
 * 0,82 она становится 10,4 × 14,2 и, сдвинутая сюда, встаёт ровно
 * посередине по обеим осям.
 */
export const MARK_FIT = 'translate(2.26 3.5) scale(0.82)';

/** Радиус скругления знака — 27 % стороны, как в эталоне (7 при 26). */
export const MARK_RADIUS = 8.6;

export function BidraMark({ size = 30 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 32 32"
      aria-hidden="true"
      style={{ flex: '0 0 auto', display: 'block' }}
    >
      <rect width="32" height="32" rx={MARK_RADIUS} fill="var(--brand)" />
      <g transform={MARK_FIT}>
        <path d={MARK_PATH} fill="var(--ink-on-brand)" />
      </g>
    </svg>
  );
}

/* ═══════════════════════════════════════════════════════════════════════
   ЗНАЧКИ ПАКЕТА 25.08. Контуры перенесены из `Bidra-Kabinet.dc.html`
   путь в путь: рисовать «похожий» значок по описанию — это ровно та
   работа, из-за которой у нас в шапке стояла шестерёнка там, где
   в дизайне ключ. Штрих у каждого свой, как в эталоне.
   ═══════════════════════════════════════════════════════════════════ */

/**
 * Мишень — «цели по деньгам». Два круга, как в эталоне: 5,6 и 2.
 *
 * Не «яблочко» из трёх колец и не флажок: строка целей стоит мелким
 * кеглем в подвале карточки, и третье кольцо на тринадцати пикселях
 * слилось бы в кляксу.
 */
export function IconTarget({ size = 13, ...p }: IconProps) {
  return (
    <Svg size={size} {...p}>
      <circle cx="8" cy="8" r="5.6" strokeWidth="1.5" />
      <circle cx="8" cy="8" r="2" strokeWidth="1.5" />
    </Svg>
  );
}

/**
 * Стрелка вниз в лоток — «выгрузить в файл».
 *
 * КОНТУР ОДИН НА ВСЕ ТРИ МЕСТА, где эталон рисует выгрузку: значок
 * в шапке биддера, в углу панели товаров и в истории оплат. Три
 * похожих стрелки однажды разъехались бы, и человек перестал бы
 * узнавать её с одного взгляда.
 */
export function IconExport({ size = 14, ...p }: IconProps) {
  return (
    <Svg size={size} {...p}>
      <path d="M8 2.4v7.2M5.2 6.9 8 9.7l2.8-2.8" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" />
      <path
        d="M2.6 11.4v1.2a1.4 1.4 0 0 0 1.4 1.4h8a1.4 1.4 0 0 0 1.4-1.4v-1.2"
        strokeLinecap="round"
        strokeWidth="1.5"
      />
    </Svg>
  );
}

/** Папка — «группа кампаний». Контур из эталона, кнопка выбора группы. */
export function IconFolder({ size = 13, ...p }: IconProps) {
  return (
    <Svg size={size} {...p}>
      <path
        d="M2.2 5.2a1.4 1.4 0 0 1 1.4-1.4h2.3l1.2 1.4h5.3a1.4 1.4 0 0 1 1.4 1.4v5a1.4 1.4 0 0 1-1.4 1.4H3.6a1.4 1.4 0 0 1-1.4-1.4z"
        strokeLinejoin="round"
      />
    </Svg>
  );
}

/** Корзина — «удалить». Контур из эталона, окно групп. */
export function IconTrash({ size = 14, ...p }: IconProps) {
  return (
    <Svg size={size} {...p}>
      <path
        d="M3.4 4.6h9.2M6.4 4.6V3.4a1 1 0 0 1 1-1h1.2a1 1 0 0 1 1 1v1.2M4.6 4.6l.5 8a1 1 0 0 0 1 .9h3.8a1 1 0 0 0 1-.9l.5-8"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.4"
      />
    </Svg>
  );
}

/** Облачко — «Поддержка». */
export function IconChat(p: IconProps) {
  return (
    <Svg {...p}>
      <path
        d="M13.4 8.2c0 2.4-2.4 4.3-5.4 4.3-.7 0-1.3-.1-1.9-.3l-3 1 1-2.3a4 4 0 0 1-1.5-2.7C2.6 5.8 5 3.9 8 3.9s5.4 1.9 5.4 4.3Z"
        strokeLinejoin="round"
        strokeWidth="1.5"
      />
    </Svg>
  );
}

/** Ключ — «Пароль и доступ». В шапке стояла шестерёнка: она про настройки. */
export function IconKey(p: IconProps) {
  return (
    <Svg штрих={1.5} {...p}>
      <circle cx="5.6" cy="10.4" r="2.9" />
      <path d="m7.7 8.3 5.3-5.3M10.5 5.5l1.7 1.7M12.4 3.6l1.7 1.7" strokeLinecap="round" />
    </Svg>
  );
}

/**
 * Ящик с ручкой — «в группе пусто». Рисунок эталона со экрана
 * «группа пуста»: группа это ящик, в который складывают кампании,
 * и пустой он выглядит именно так.
 */
export function IconBox(p: IconProps) {
  return (
    <Svg штрих={1.2} {...p}>
      <rect x="2" y="4.2" width="12" height="8.6" rx="1.6" />
      <path d="M5.6 4.2V3a.8.8 0 0 1 .8-.8h3.2a.8.8 0 0 1 .8.8v1.2" strokeLinecap="round" />
    </Svg>
  );
}

/** Карточка — «Счета и оплаты». */
export function IconCard(p: IconProps) {
  return (
    <Svg {...p}>
      <rect x="2" y="3.6" width="12" height="8.8" rx="1.6" strokeWidth="1.5" />
      <path d="M2 6.6h12M4.6 9.8h3" strokeLinecap="round" strokeWidth="1.5" />
    </Svg>
  );
}

/** Телефон — «Добавить на телефон». */
export function IconPhone(p: IconProps) {
  return (
    <Svg {...p}>
      <rect x="4.4" y="1.8" width="7.2" height="12.4" rx="1.6" strokeWidth="1.5" />
      <path d="M7.2 12.2h1.6" strokeLinecap="round" strokeWidth="1.5" />
    </Svg>
  );
}

/** Бирка — «Сборка». */
export function IconTag(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M7.6 2.4H3.2a.8.8 0 0 0-.8.8v4.4l6.4 6.4 5.2-5.2z" strokeLinejoin="round" strokeWidth="1.5" />
      <circle cx="5.6" cy="5.6" r=".9" strokeWidth="1.5" />
    </Svg>
  );
}

/** Дверь со стрелкой — «Выйти». */
export function IconExit(p: IconProps) {
  return (
    <Svg {...p}>
      <path
        d="M9.6 11.4v1.4a1.2 1.2 0 0 1-1.2 1.2H3.6a1.2 1.2 0 0 1-1.2-1.2V3.2A1.2 1.2 0 0 1 3.6 2h4.8a1.2 1.2 0 0 1 1.2 1.2v1.4M11.6 5.6 14 8l-2.4 2.4M6.4 8H14"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.5"
      />
    </Svg>
  );
}

/** Полотно с колонкой — «Биддер». */
export function IconBidder(p: IconProps) {
  return (
    <Svg {...p}>
      <rect x="2" y="3" width="12" height="10" rx="1.6" strokeWidth="1.5" />
      <path d="M2 6.6h12M6.4 6.6V13" strokeLinecap="round" strokeWidth="1.5" />
    </Svg>
  );
}

/** Столбики — «Финансовая аналитика». */
export function IconBars2(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M2.4 13.4h11.2" strokeLinecap="round" strokeWidth="1.5" />
      <path d="M4.4 11V7.2M8 11V3.4M11.6 11V5.6" strokeLinecap="round" strokeWidth="1.5" />
    </Svg>
  );
}

/** Два прямоугольника — «Кабинеты Wildberries». */
export function IconCabinets(p: IconProps) {
  return (
    <Svg {...p}>
      <rect x="2" y="4.8" width="8.6" height="8.4" rx="1.4" strokeWidth="1.5" />
      <path
        d="M5.4 4.8V4a1.4 1.4 0 0 1 1.4-1.4h6.2A1.4 1.4 0 0 1 14.4 4v6.2a1.4 1.4 0 0 1-1.4 1.4h-.9"
        strokeLinecap="round"
        strokeWidth="1.5"
      />
    </Svg>
  );
}

/**
 * Два человека — «Участники и права».
 *
 * В эталоне такого пункта нет: раздела «Участники» дизайн не знает
 * (вопрос 98). Значок нарисован в той же сетке 16×16 и тем же штрихом,
 * что остальные, — чтобы наш пункт не выделялся в списке чужим весом
 * линии, пока дизайнер не скажет, куда его девать.
 */
export function IconUsers(p: IconProps) {
  return (
    <Svg {...p}>
      <circle cx="6.2" cy="5.6" r="2.4" strokeWidth="1.5" />
      <path d="M1.9 13.4a4.3 4.3 0 0 1 8.6 0" strokeLinecap="round" strokeWidth="1.5" />
      <path d="M11 3.6a2.4 2.4 0 0 1 0 4M12.4 9.6a4.3 4.3 0 0 1 1.7 3.8" strokeLinecap="round" strokeWidth="1.5" />
    </Svg>
  );
}


/** Кружок с восклицанием — оговорка, а не беда. Контур из эталона. */
export function IconInfo({ size = 16, ...p }: IconProps) {
  return (
    <Svg size={size} штрих={1.5} {...p}>
      <circle cx="8" cy="8" r="6" />
      <path d="M8 7.2v4M8 4.6v.9" strokeLinecap="round" />
    </Svg>
  );
}

/**
 * Снимок экрана — «приложить фото» в поддержке. Контур из эталона:
 * рамка 11,6 × 9,2 со скруглением 1,4, кружок объектива и линия гор.
 */
export function IconPhoto({ size = 15, ...p }: IconProps) {
  return (
    <Svg size={size} {...p}>
      <rect x="2.2" y="3.4" width="11.6" height="9.2" rx="1.4" strokeWidth="1.6" />
      <circle cx="6" cy="6.6" r="1" strokeWidth="1.6" />
      <path
        d="m3.4 11.4 3-3 2.4 2.2 2-1.8 2 2.6"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.6"
      />
    </Svg>
  );
}

/**
 * Две страницы одна за другой — скопировать номер. (П-25, П-28)
 *
 * Нарисован по эталону: прямоугольник 7,2 × 7,2 со скруглением 1,4
 * и загиб второй страницы за ним. Отдельно от `IconExport`: там
 * стрелка наружу («отдать файл»), здесь два листа («то же самое
 * в буфере»), и путать их нельзя — первое открывает окно загрузки.
 */
export function IconCopy({ size = 13, ...p }: IconProps) {
  return (
    <Svg size={size} {...p}>
      <rect x="5.4" y="5.4" width="7.2" height="7.2" rx="1.4" strokeWidth="1.4" />
      <path d="M10.2 3.4H4.8a1.4 1.4 0 0 0-1.4 1.4v5.4" strokeLinecap="round" strokeWidth="1.4" />
    </Svg>
  );
}

/**
 * Круговая стрелка возврата — вернуть товар в продажу. (П-36)
 *
 * Нарисована по эталону (`M3.4 8a4.6 4.6 0 1 0 1.5-3.4M3.2 3v2.6h2.6`).
 * Отдельно от `IconReturn`: там стрелка, уходящая назад и вниз
 * («вернуть запрос в показы»), здесь круг с хвостиком («отменить
 * снятие»). Один знак на два разных действия однажды прочитали бы
 * неверно, а здесь цена неверной разгадки — прибыль товара.
 */
export function IconUndo({ size = 14, ...p }: IconProps) {
  return (
    <Svg size={size} {...p}>
      <path
        d="M3.4 8a4.6 4.6 0 1 0 1.5-3.4M3.2 3v2.6h2.6"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.5"
      />
    </Svg>
  );
}

/**
 * Перечёркнутый круг — снять товар с продажи. (П-36)
 *
 * Отдельно от `IconBan` (круг 5,6 и черта из угла в угол): у эталона
 * здесь круг 5,4 и черта покороче, а рядом, в той же строке, стоит
 * слово «Снять». Значок без подписи тут и не годится — ровно это
 * и было бедой прежнего меню из трёх точек.
 */
export function IconOff({ size = 14, ...p }: IconProps) {
  return (
    <Svg size={size} {...p}>
      <circle cx="8" cy="8" r="5.4" strokeWidth="1.5" />
      <path d="m4.4 11.6 7.2-7.2" strokeLinecap="round" strokeWidth="1.5" />
    </Svg>
  );
}
